import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import PayrollKPICard from './components/PayrollKPICard';
import DeadlineCountdown from './components/DeadlineCountdown';

import PayrollComplianceChart from './components/PayrollComplianceChart';
import PayrollBreakdownTable from './components/PayrollBreakdownTable';
import PayPeriodSelector from './components/PayPeriodSelector';
import AutomatedFilingCard from './components/AutomatedFilingCard';
import LodgmentTrackingTable from './components/LodgmentTrackingTable';
import ConfirmationRecordsList from './components/ConfirmationRecordsList';
import Icon from '../../components/AppIcon';
import governmentFilingService from '../../services/governmentFilingService';
import PayrollAlertWidget from './components/PayrollAlertWidget';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import { employeeService } from '../../services/employeeService';
import realtimeDashboardService from '../../services/realtimeDashboardService';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

import BiometricSecurityDisclaimer from '../../components/navigation/BiometricSecurityDisclaimer';

const PayrollCompliance = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('2026-01');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    dateRange: { start: '', end: '' },
    employmentType: ''
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [lodgments, setLodgments] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLodgment, setSelectedLodgment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [kpiData, setKpiData] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [employeePayrollData, setEmployeePayrollData] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();

    // Subscribe to real-time payroll updates
    const channels = realtimeDashboardService?.subscribeToAllUpdates(
      {
        onViolation: () => {
          loadData(); // Refresh payroll data
        },
        onPayrollError: (error) => {
          console.log('Real-time payroll error:', error);
          loadData(); // Refresh payroll data
        },
        onDeadline: (deadline) => {
          console.log('Real-time deadline update:', deadline);
          loadData(); // Refresh payroll data
        }
      },
      filters?.department || 'all'
    );

    return () => {
      realtimeDashboardService?.unsubscribeAll(channels);
    };
  }, [selectedPeriod, filters?.department]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const employeesData = await employeeService?.getAll();
      setEmployees(employeesData || []);

      if (employeesData?.length > 0) {
        const [lodgmentsData, confirmationsData] = await Promise.all([
          governmentFilingService?.getAllLodgments(),
          governmentFilingService?.getAllConfirmations()
        ]);

        setLodgments(lodgmentsData || []);
        setConfirmations(confirmationsData || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load payroll compliance data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleAddEmployees = () => {
    window.location.href = '/workforce-management-dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading payroll data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no employees
  if (!employees || employees?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Payroll Compliance Dashboard
            </h1>
            <p className="caption text-sm md:text-base text-muted-foreground">
              NASFUND, SWT, and minimum wage compliance monitoring for PNG regulations
            </p>
          </div>

          <div className="bg-card rounded-lg border border-border p-8">
            <EmptyStatePrompt
              icon="DollarSign"
              title="No Payroll Records Available"
              description="Add employees to your workforce to enable payroll compliance tracking, NASFUND contribution monitoring, SWT filing automation, and minimum wage verification."
              actionLabel="Add Employees"
              onAction={handleAddEmployees}
            />
          </div>
        </main>
      </div>
    );
  }

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSubmitFiling = async (lodgmentId) => {
    try {
      await governmentFilingService?.submitFilingToPortal(lodgmentId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to submit filing');
    }
  };

  const handleRetryFiling = async (lodgmentId) => {
    try {
      await governmentFilingService?.submitFilingToPortal(lodgmentId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to retry filing');
    }
  };

  const handleViewDetails = (lodgment) => {
    setSelectedLodgment(lodgment);
  };

  const formatCurrency = (amount) => {
    return `K${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <BiometricSecurityDisclaimer />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <DataRefreshIndicator isRefreshing={isRefreshing} />

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2">
              Payroll Compliance Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Monitor NASFUND contributions, SWT filings, and minimum wage compliance
            </p>
          </div>
          <PayPeriodSelector 
            selectedPeriod={selectedPeriod} 
            onPeriodChange={setSelectedPeriod}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        </div>

        {/* Tabs */}
        <div className="bg-card border border-border rounded-lg p-1 flex gap-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              activeTab === 'overview' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('automated-filing')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              activeTab === 'automated-filing' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            Automated Filing
          </button>
          <button
            onClick={() => setActiveTab('tracking')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              activeTab === 'tracking' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            Lodgment Tracking
          </button>
          <button
            onClick={() => setActiveTab('confirmations')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              activeTab === 'confirmations' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            Confirmations
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {kpiData?.map((kpi, index) => (
                <PayrollKPICard key={index} {...kpi} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <PayrollAlertWidget />
              <div className="space-y-6">
                {upcomingDeadlines?.slice(0, 2)?.map((deadline) => (
                  <DeadlineCountdown key={deadline?.id} {...deadline} />
                ))}
              </div>
            </div>

            {/* Compliance Chart */}
            <PayrollComplianceChart data={chartData} />

            {/* Employee Breakdown Table */}
            <PayrollBreakdownTable employees={employeePayrollData} />
          </>
        )}

        {activeTab === 'automated-filing' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading font-semibold text-lg text-foreground mb-1">
                    Automated Government Filings
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Auto-generated NASFUND and SWT filings ready for submission
                  </p>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading filings...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {lodgments?.filter(l => l?.lodgmentStatus === 'pending' || l?.lodgmentStatus === 'failed')?.map((lodgment) => (
                  <AutomatedFilingCard
                    key={lodgment?.id}
                    lodgment={lodgment}
                    onSubmit={handleSubmitFiling}
                    onRetry={handleRetryFiling}
                    onViewDetails={handleViewDetails}
                  />
                ))}
                {lodgments?.filter(l => l?.lodgmentStatus === 'pending' || l?.lodgmentStatus === 'failed')?.length === 0 && (
                  <div className="col-span-2 text-center py-12 bg-card border border-border rounded-lg">
                    <Icon name="CheckCircle2" size={48} color="var(--color-success)" className="mx-auto mb-4" />
                    <p className="text-foreground font-medium mb-2">All filings up to date</p>
                    <p className="text-sm text-muted-foreground">No pending submissions at this time</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tracking' && (
          <div className="space-y-6">
            <LodgmentTrackingTable
              lodgments={lodgments}
              onViewDetails={handleViewDetails}
            />
          </div>
        )}

        {activeTab === 'confirmations' && (
          <div className="space-y-6">
            <ConfirmationRecordsList confirmations={confirmations} />
          </div>
        )}
      </main>

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default PayrollCompliance;